import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import type { LayoutNode } from "./layout";

export interface Transform {
	x: number;
	y: number;
	scale: number;
}

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 2;
const ZOOM_DIVISOR = 260;
const DRAG_THRESHOLD = 5;
const LINE_DELTA_PIXELS = 16;

interface UseViewportOptions {
	viewportRef: RefObject<HTMLDivElement | null>;
	contentRef: RefObject<HTMLDivElement | null>;
	onTap?: (event: React.PointerEvent) => void;
}

export function useViewport({ viewportRef, contentRef, onTap }: UseViewportOptions) {
	const transformRef = useRef<Transform>({ x: 0, y: 0, scale: 1 });
	const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, scale: 1 });
	const rafIdRef = useRef<number>(0);
	const wasDragRef = useRef(false);

	const applyTransform = useCallback(
		(nextTransform: Transform) => {
			const element = contentRef.current;
			if (!element) return;
			element.style.transform = `translate(${nextTransform.x}px, ${nextTransform.y}px) scale(${nextTransform.scale})`;
		},
		[contentRef]
	);

	const syncToState = useCallback(() => {
		if (rafIdRef.current) return;
		rafIdRef.current = requestAnimationFrame(() => {
			rafIdRef.current = 0;
			setTransform({ ...transformRef.current });
		});
	}, []);

	const dragState = useRef<{
		pointerId: number;
		startX: number;
		startY: number;
		startTx: number;
		startTy: number;
		totalMovement: number;
		isDragging: boolean;
	} | null>(null);

	const onPointerDown = useCallback(
		(event: React.PointerEvent) => {
			if (event.button !== 0) return;
			if ((event.target as HTMLElement).closest("[data-interactive]")) return;

			const currentTransform = transformRef.current;
			dragState.current = {
				pointerId: event.pointerId,
				startX: event.clientX,
				startY: event.clientY,
				startTx: currentTransform.x,
				startTy: currentTransform.y,
				totalMovement: 0,
				isDragging: false,
			};
			wasDragRef.current = false;

			viewportRef.current?.setPointerCapture(event.pointerId);
		},
		[viewportRef]
	);

	const onPointerMove = useCallback(
		(event: React.PointerEvent) => {
			const state = dragState.current;
			if (!state || state.pointerId !== event.pointerId) return;

			const dx = event.clientX - state.startX;
			const dy = event.clientY - state.startY;
			state.totalMovement += Math.abs(event.movementX) + Math.abs(event.movementY);

			if (!state.isDragging && state.totalMovement > DRAG_THRESHOLD) {
				state.isDragging = true;
			}

			if (state.isDragging) {
				const currentTransform = transformRef.current;
				currentTransform.x = state.startTx + dx;
				currentTransform.y = state.startTy + dy;
				applyTransform(currentTransform);
				syncToState();
			}
		},
		[applyTransform, syncToState]
	);

	const onPointerUp = useCallback(
		(event: React.PointerEvent) => {
			const state = dragState.current;
			if (!state || state.pointerId !== event.pointerId) return;

			viewportRef.current?.releasePointerCapture(event.pointerId);
			wasDragRef.current = state.isDragging;
			const wasTap = !state.isDragging;
			dragState.current = null;
			setTransform({ ...transformRef.current });
			if (wasTap && onTap) onTap(event);
		},
		[viewportRef, onTap]
	);

	const zoomAtPointer = useCallback(
		(event: WheelEvent) => {
			const viewportElement = viewportRef.current;
			if (!viewportElement) return;

			event.preventDefault();
			event.stopPropagation();

			const currentTransform = transformRef.current;
			const rect = viewportElement.getBoundingClientRect();
			const cursorX = event.clientX - rect.left;
			const cursorY = event.clientY - rect.top;
			const contentX = (cursorX - currentTransform.x) / currentTransform.scale;
			const contentY = (cursorY - currentTransform.y) / currentTransform.scale;

			let normalizedDelta = event.deltaY;
			if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
				normalizedDelta *= LINE_DELTA_PIXELS;
			} else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
				normalizedDelta *= viewportElement.clientHeight;
			}

			const zoomFactor = Math.exp(-normalizedDelta / ZOOM_DIVISOR);
			const nextScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, currentTransform.scale * zoomFactor));
			const nextTransform = {
				scale: nextScale,
				x: cursorX - contentX * nextScale,
				y: cursorY - contentY * nextScale,
			};

			transformRef.current = nextTransform;
			applyTransform(nextTransform);
			syncToState();
		},
		[applyTransform, syncToState, viewportRef]
	);

	useEffect(() => {
		const onWheel = (event: WheelEvent) => {
			const viewportElement = viewportRef.current;
			const target = event.target;
			if (!viewportElement || !(target instanceof Node) || !viewportElement.contains(target)) return;
			zoomAtPointer(event);
		};

		document.addEventListener("wheel", onWheel, { passive: false, capture: true });
		return () => document.removeEventListener("wheel", onWheel, { capture: true });
	}, [viewportRef, zoomAtPointer]);

	const setView = useCallback(
		(view: Transform, duration = 0) => {
			const contentElement = contentRef.current;
			if (!contentElement) return;
			transformRef.current = { ...view };

			if (duration > 0) {
				contentElement.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
				applyTransform(view);
				const onEnd = () => {
					contentElement.style.transition = "";
					contentElement.removeEventListener("transitionend", onEnd);
				};
				contentElement.addEventListener("transitionend", onEnd, { once: true });
			} else {
				applyTransform(view);
			}

			setTransform({ ...view });
		},
		[contentRef, applyTransform]
	);

	const fitView = useCallback(
		(nodes: LayoutNode[], duration = 500) => {
			const viewportElement = viewportRef.current;
			if (!viewportElement || nodes.length === 0) return;

			let minX = Infinity;
			let minY = Infinity;
			let maxX = -Infinity;
			let maxY = -Infinity;
			for (const node of nodes) {
				minX = Math.min(minX, node.x);
				minY = Math.min(minY, node.y);
				maxX = Math.max(maxX, node.x + node.width);
				maxY = Math.max(maxY, node.y + node.height);
			}

			const padding = 48;
			const viewportRect = viewportElement.getBoundingClientRect();
			const contentWidth = maxX - minX;
			const contentHeight = maxY - minY;
			const availableWidth = viewportRect.width - padding * 2;
			const availableHeight = viewportRect.height - padding * 2;
			const scale = Math.min(1.35, availableWidth / contentWidth, availableHeight / contentHeight);
			const x = (viewportRect.width - contentWidth * scale) / 2 - minX * scale;
			const y = (viewportRect.height - contentHeight * scale) / 2 - minY * scale;

			setView({ x, y, scale }, duration);
		},
		[viewportRef, setView]
	);

	const centerOn = useCallback(
		(x: number, y: number, targetScale = 1.15, duration = 700) => {
			const viewportElement = viewportRef.current;
			if (!viewportElement) return;

			const viewportRect = viewportElement.getBoundingClientRect();
			setView(
				{
					x: viewportRect.width / 2 - x * targetScale,
					y: viewportRect.height / 2 - y * targetScale,
					scale: targetScale,
				},
				duration
			);
		},
		[viewportRef, setView]
	);

	const zoomTo = useCallback(
		(scale: number) => {
			const viewportElement = viewportRef.current;
			if (!viewportElement) return;

			const clampedScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
			const viewportRect = viewportElement.getBoundingClientRect();
			const currentTransform = transformRef.current;
			const centerX = viewportRect.width / 2;
			const centerY = viewportRect.height / 2;
			const contentX = (centerX - currentTransform.x) / currentTransform.scale;
			const contentY = (centerY - currentTransform.y) / currentTransform.scale;

			setView({
				x: centerX - contentX * clampedScale,
				y: centerY - contentY * clampedScale,
				scale: clampedScale,
			});
		},
		[viewportRef, setView]
	);

	const wasDrag = useCallback(() => wasDragRef.current, []);

	useEffect(() => {
		return () => {
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
			}
		};
	}, []);

	return {
		transform,
		fitView,
		centerOn,
		zoomTo,
		setView,
		wasDrag,
		handlers: {
			onPointerDown,
			onPointerMove,
			onPointerUp,
		},
	};
}
